import { Alert, Box, Button, CircularProgress, Stack } from "@mui/material";
// import AccordianGlassCard from "Components/UI/AccordianGlassCard";
import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import {
  ApiAIPromptStatus,
  ApiAIPromptType,
  ApiChartType,
} from "@incendium/api";
import { useCallback, useMemo, useState } from "react";
import { ApiAIPrompt } from "@incendium/api";
import StyledMarkdown from "./StyledMarkdown";
import { AnalyticsChartStatCard } from "features/analytics";
import { Refresh, Save } from "@mui/icons-material";
import { useAiContext } from "../providers/AiProvider";
import { useSelectedProject } from "Hooks";
import { ReactComponent as AiIcon } from "Assets/icons/menu/ai.svg";
import { IChart } from "Interfaces";
import { useNotification } from "Hooks";
import { useCharts } from "Hooks/useCharts";
import AnalystResponse from "./AnalystResponse";
import FinalSummaryResponse from "./FinalSummaryResponse";
import ReviewerResponse from "./ReviewerResponse";
import AnalyticsCardGroup from "features/analytics/components/AnalyticsCardGroup";

interface IAiResponseProps {
  message: ApiAIPrompt;
}

const MCP_UPDATE_MARKER = "[[MCP_UPDATE]]";
const MCP_CHART_PAYLOAD_MARKER = "[[MCP_CHART_PAYLOAD_B64]]";

type McpStreamSegment =
  | { kind: "update"; message: string }
  | { kind: "chart"; payload: unknown }
  | { kind: "text"; text: string };

// Helper function to check if a string looks like JSON
const looksLikeJSON = (str: string): boolean => {
  const trimmed = str.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
};

/** Preserves stream order: updates, chart payloads, and plain text (insights, final JSON) stay interleaved as emitted. */
const parseMcpEnhancements = (raw: string): McpStreamSegment[] => {
  const segments: McpStreamSegment[] = [];
  const lines = raw.split(/\r?\n/);
  const textBuf: string[] = [];

  const flushText = () => {
    const joined = textBuf.join("\n");
    textBuf.length = 0;
    if (joined.trim()) {
      segments.push({ kind: "text", text: joined });
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith(MCP_UPDATE_MARKER)) {
      flushText();
      const msg = trimmed.slice(MCP_UPDATE_MARKER.length).trim();
      if (msg) {
        segments.push({ kind: "update", message: msg });
      }
      continue;
    }
    if (trimmed.startsWith(MCP_CHART_PAYLOAD_MARKER)) {
      flushText();
      const b64 = trimmed.slice(MCP_CHART_PAYLOAD_MARKER.length).trim();
      if (b64) {
        try {
          const decoded = atob(b64);
          segments.push({ kind: "chart", payload: JSON.parse(decoded) });
        } catch {
          // Ignore invalid marker payloads.
        }
      }
      continue;
    }
    textBuf.push(line);
  }
  flushText();
  return segments;
};

const isAnalystStyleResponse = (response: string): boolean => {
  const s = response.trim();
  if (!s) {
    return false;
  }
  return (
    s.includes('"analysis_summary"') ||
    s.includes('"critical_insights"') ||
    s.includes('"key_recommendations"')
  );
};

function AiResponse({ message }: IAiResponseProps) {
  const { retryPrompt } = useAiContext();
  const { selectedClient, selectedProject } = useSelectedProject();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { saveNewChart } = useCharts();

  const [saving, setSaving] = useState(false);

  const onSave = useCallback(
    async (chart: IChart) => {
      setSaving(true);
      try {
        await saveNewChart(chart, selectedProject);
        showSuccessNotification("Chart Saved to Library");
      } catch (error) {
        showErrorNotification("Something Went Wrong");
      } finally {
        setSaving(false);
      }
    },
    [
      selectedProject?.id,
      saveNewChart,
      showSuccessNotification,
      showErrorNotification,
    ],
  );

  const aiPayloadToIChart = useCallback((chartPayload: any): IChart => {
    if ("yAxisKeys" in chartPayload.chart) {
      return chartPayload.chart as IChart;
    }

    // fallback for old version
    return {
      name: chartPayload.chart.name,
      dimension: chartPayload.chart.dimension,
      template: chartPayload.chart.template,
      type: chartPayload.chart.type,
      attributes: chartPayload.chart.attributes || [],
      yAxisKeys: chartPayload.chart.y_axis_keys,
      orderBy: chartPayload.chart.order_by,
      limit: chartPayload.chart.limit,
      comparison: chartPayload.chart.comparison,
      includeEmpty: chartPayload.chart.include_empty,
      description: chartPayload.chart.description,
      withTotals: chartPayload.chart.with_totals,
    };
  }, []);

  const renderResponse = useMemo(() => {
    if (message.status === ApiAIPromptStatus.AI_PROMPT_STATUS_FAILED) {
      return (
        <Alert
          severity="error"
          action={
            <Button
              startIcon={<Refresh />}
              onClick={() => {
                retryPrompt(message.id);
              }}
            >
              Retry
            </Button>
          }
        >
          {message.error ||
            message.response ||
            "Sorry, there was an error processing your request."}
        </Alert>
      );
    }

    if (message.type === ApiAIPromptType.AI_PROMPT_TYPE_ANALYST) {
      return <AnalystResponse response={message.response || ""} />;
    }

    if (message.type === ApiAIPromptType.AI_PROMPT_TYPE_FINAL_SUMMARY) {
      return <FinalSummaryResponse response={message.response || ""} />;
    }

    if (message.type === ApiAIPromptType.AI_PROMPT_TYPE_REVIEWER) {
      return <ReviewerResponse response={message.response || ""} />;
    }

    if (message.type === ApiAIPromptType.AI_PROMPT_TYPE_CHART) {
      if (!message.response) {
        return <></>;
      }

      if (!looksLikeJSON(message.response)) {
        return <StyledMarkdown>{message.response}</StyledMarkdown>;
      }

      const chartPayload = JSON.parse(message.response);

      const chart = aiPayloadToIChart(chartPayload);

      if (chart.type === ApiChartType.STAT_CARD) {
        return (
          <Box sx={{ maxWidth: 450 }}>
            <AnalyticsChartStatCard
              noToolbar
              chartOutput={{
                lastNDays: chartPayload.last_n_days,
                from: chartPayload.from,
                to: chartPayload.to,
              }}
              overrideComparisonOutput={{
                lastNDays: undefined,
                from: chartPayload.comparison_from,
                to: chartPayload.comparison_to,
              }}
              chart={{
                ...chart,
                // comparison: chartPayload.chart.comparison,
                // includeEmpty: chartPayload.chart.include_empty,
                // description: chartPayload.chart.description,
                // withTotals: chartPayload.chart.with_totals,
              }}
            />
          </Box>
        );
      }

      return (
        <Stack spacing={1} justifyContent="flex-start">
          <AnalyticsCard
            height={590}
            overrideDate={{
              lastNDays: chartPayload.last_n_days,
              from: chartPayload.from,
              to: chartPayload.to,
            }}
            overrideComparisonDate={
              chartPayload.comparison_from && chartPayload.comparison_to
                ? {
                    lastNDays: undefined,
                    from: chartPayload.comparison_from,
                    to: chartPayload.comparison_to,
                  }
                : undefined
            }
            noToolbar
            chart={{
              ...chart,
              displayOptions: {
                ...chart.displayOptions,
                noTitle: true,
                noFilters: true,
              },
              // comparison: chartPayload.chart.comparison,
              // includeEmpty: chartPayload.chart.include_empty,
              // description: chartPayload.chart.description,
              // withTotals: chartPayload.chart.with_totals,
            }}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-start">
            <Button
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              disabled={saving}
              variant="text"
              onClick={() => {
                if (saving) {
                  return;
                }
                onSave(chart);
              }}
            >
              Save to Library
            </Button>
          </Stack>
        </Stack>
      );
    }

    if (message.status === ApiAIPromptStatus.AI_PROMPT_STATUS_CANCELLED) {
      return (
        <Alert severity="info">Generation stopped. Cancelled by user.</Alert>
      );
    }

    // fix response, if it contains /clients/:clientId/projects/:projectId/ replace it with actual project and client id's
    // And if markdown for link is just [] replace it with [path](path) where path is the actual path
    const rawResponse = (message.response || "").replaceAll(
      `clients/:clientId/projects/:projectId`,
      `clients/${selectedClient?.id}/projects/${selectedProject?.id}`,
    );

    if (
      message.type === ApiAIPromptType.AI_PROMPT_TYPE_MCP_AGENT &&
      (rawResponse.includes(MCP_UPDATE_MARKER) ||
        rawResponse.includes(MCP_CHART_PAYLOAD_MARKER))
    ) {
      const segments = parseMcpEnhancements(rawResponse);
      let chartIdx = 0;
      return (
        <Stack spacing={1}>
          {segments.map((seg, idx) => {
            if (seg.kind === "update") {
              return (
                <Alert key={`mcp-seg-${idx}`} severity="info">
                  {seg.message}
                </Alert>
              );
            }
            if (seg.kind === "text") {
              const t = seg.text.trim();
              if (!t) {
                return null;
              }
              return isAnalystStyleResponse(t) ? (
                <AnalystResponse key={`mcp-seg-${idx}`} response={t} />
              ) : (
                <StyledMarkdown key={`mcp-seg-${idx}`}>{t}</StyledMarkdown>
              );
            }
            const payload = seg.payload as Record<string, any>;
            const chart = aiPayloadToIChart(payload);
            const accordionKey = `mcp-chart-${chartIdx++}`;
            let accordionTitle = `View ${chart.name?.trim()}`;
            if (!accordionTitle.toLowerCase().includes("chart")) {
              accordionTitle = `View ${chart.name?.trim()} Chart`;
            }
            if (chart.type === ApiChartType.STAT_CARD) {
              return (
                <Box py={2}>
                  <AnalyticsCardGroup
                    key={accordionKey}
                    title={accordionTitle}
                    defaultClosed
                    renderBody={() => (
                      <Box sx={{ maxWidth: 450 }}>
                        <AnalyticsChartStatCard
                          noToolbar
                          chartOutput={{
                            lastNDays: payload.last_n_days || payload.lastNDays,
                            from: payload.from,
                            to: payload.to,
                          }}
                          overrideComparisonOutput={{
                            lastNDays: undefined,
                            from:
                              payload.comparison_from || payload.comparisonFrom,
                            to: payload.comparison_to || payload.comparisonTo,
                          }}
                          chart={chart}
                        />
                      </Box>
                    )}
                  />
                </Box>
              );
            }
            return (
              <Box py={2}>
                <AnalyticsCardGroup
                  key={accordionKey}
                  title={accordionTitle}
                  defaultClosed
                  renderBody={() => (
                    <Stack spacing={1} justifyContent="flex-start">
                      <AnalyticsCard
                        height={590}
                        overrideDate={{
                          lastNDays: payload.last_n_days || payload.lastNDays,
                          from: payload.from,
                          to: payload.to,
                        }}
                        overrideComparisonDate={
                          (payload.comparison_from && payload.comparison_to) ||
                          (payload.comparisonFrom && payload.comparisonTo)
                            ? {
                                lastNDays: undefined,
                                from:
                                  payload.comparison_from ||
                                  payload.comparisonFrom,
                                to:
                                  payload.comparison_to || payload.comparisonTo,
                              }
                            : undefined
                        }
                        noToolbar
                        chart={{
                          ...chart,
                          displayOptions: {
                            ...chart.displayOptions,
                            noTitle: true,
                          },
                        }}
                      />
                      <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-start"
                      >
                        <Button
                          startIcon={
                            saving ? <CircularProgress size={20} /> : <Save />
                          }
                          disabled={saving}
                          variant="text"
                          onClick={() => {
                            if (saving) {
                              return;
                            }
                            onSave(chart);
                          }}
                        >
                          Save to Chart Library
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                />
              </Box>
            );
          })}
        </Stack>
      );
    }

    if (
      message.type === ApiAIPromptType.AI_PROMPT_TYPE_MCP_AGENT &&
      isAnalystStyleResponse(rawResponse)
    ) {
      return <AnalystResponse response={rawResponse} />;
    }

    return <StyledMarkdown>{rawResponse}</StyledMarkdown>;
  }, [
    message.id,
    message.type,
    message.response,
    message.error,
    message.status,
    saving,
    onSave,
    selectedClient,
    selectedProject,
    aiPayloadToIChart,
    retryPrompt,
  ]);

  return (
    <Stack>
      {(message.type === ApiAIPromptType.AI_PROMPT_TYPE_ROUTER ||
        message.type === ApiAIPromptType.AI_PROMPT_TYPE_MCP_AGENT) && (
        <Box mb={2}>
          <AiIcon width={20} height={20} />
        </Box>
      )}
      {renderResponse}
    </Stack>
  );
}

export default AiResponse;
